import React from 'react';
import { Crown } from 'lucide-react';
import Tooltip from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { useOpenUpgradeForFeature } from 'modules/entitlements';

interface PremiumBadgeProps {
  /** Boolean capability key this affordance is gated by (e.g. worksheet_pdf). */
  featureKey: string;
  /** Explanation shown on hover (a plain string is also used as the aria-label). */
  tooltip?: React.ReactNode;
  /** Short label next to the crown. Defaults to "Premium". */
  label?: string;
  className?: string;
  /** Extra classes for the tooltip bubble, e.g. to right-align it when the badge sits at a container's edge. */
  tooltipClassName?: string;
}

/** Stops propagation so it can sit on a clickable row without firing that row's action. UX only. */
export function PremiumBadge({ featureKey, tooltip, label = 'Premium', className, tooltipClassName }: PremiumBadgeProps) {
  const openUpgrade = useOpenUpgradeForFeature();

  const badge = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openUpgrade(featureKey);
      }}
      aria-label={typeof tooltip === 'string' ? tooltip : label}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5',
        'text-[11px] font-semibold text-foreground/80 transition-colors',
        'hover:border-foreground/30 hover:text-foreground',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <Crown className="size-3" aria-hidden />
      {label}
    </button>
  );

  return tooltip ? (
    <Tooltip
      content={tooltip}
      side="top"
      className={cn(
        'w-64 max-w-[calc(100vw-2rem)] whitespace-normal px-3.5 py-2 text-center text-[13px] leading-relaxed',
        tooltipClassName,
      )}
    >
      {badge}
    </Tooltip>
  ) : (
    badge
  );
}

export default PremiumBadge;
